import { useEffect, useState } from 'react';
import RestUtilities from '../../services/RestUtilities';

import {
  MenuItem,
  Box,
  Typography,
  SelectChangeEvent,
  Button,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SmallSelect from '../../components/Common/SmallSelect';
import {
  loadingState,
  paginationModelState,
  toastState,
} from '../../state/RecoilState';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Enquiry } from '../../@types/api';
import dayjs from 'dayjs';
import { colDateFormat, enquiryStatus } from '../../Constants';

const Enquiries = () => {
  const navigate = useNavigate();
  const setToast = useSetRecoilState(toastState);
  const [data, setData] = useState<[] | undefined>(undefined);
  const [paginationModel, setPaginationModel] =
    useRecoilState(paginationModelState);
  const setLoading = useSetRecoilState(loadingState);
  const [status, setStatus] = useState(enquiryStatus.OPEN);

  useEffect(() => {
    const getAllData = async () => {
      setLoading(true);
      const response = await RestUtilities.get('/api/enquiry/GetAll');
      setData(response.content);
      setToast('Enquiries Loaded');
      setLoading(false);
    };
    if (!data) {
      getAllData();
    }
  }, [data, setLoading, setToast]);

  const openInPage = (rowData: Enquiry) => {
    navigate(`/enquiry/${rowData.enquiryId}`);
  };

  const handleStatusChange = (event: SelectChangeEvent<unknown>) => {
    setStatus(Number(event.target.value));
  };

  const handleRespond = (event: any, rowData: Enquiry) => {
    openInPage(rowData);
  };

  const handleConvertToBooking = (event: any, rowData: Enquiry) => {
    navigate(`/createbooking/${rowData.enquiryId}`);
  };

  const columns: GridColDef[] = [
    {
      headerName: 'ID',
      field: 'enquiryId',
      type: 'number',
      sortable: true,
      headerAlign: 'left',
      width: 80,
      renderCell: (params) => (
        <Button
          onClick={(event) => handleRespond(event, params.row)}
          size='small'
          variant='contained'
          sx={{ my: 3 }}
          color='primary'
        >
          {params.row.enquiryId}
        </Button>
      ),
    },
    {
      headerName: 'Firstname',
      field: 'firstName',
    },
    {
      headerName: 'Surname',
      field: 'surname',
    },
    {
      headerName: 'Email',
      field: 'emailAddress',
      width: 250,
    },
    {
      headerName: 'Comments',
      field: 'comments',
      sortable: false,
      minWidth: 300,
      flex: 1,
      valueGetter: (params) => params.value,
      renderCell: (params) => (
        <Typography noWrap variant='body2'>
          {params.value?.replace('. No need to process', '')}
        </Typography>
      ),
    },
    {
      headerName: 'Event Date',
      field: 'eventDate',
      sortable: false,
      filterable: false,
      valueGetter: (params) => dayjs(params.value).format(colDateFormat),
    },
    {
      headerName: 'Venue',
      width: 200,
      field: 'venueName',
      sortable: true,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      filterable: false,
      width: 144,
      renderCell: (params) => (
        <div>
          <Button
            onClick={(event) => handleConvertToBooking(event, params.row)}
            size='small'
            variant='contained'
            color='secondary'
          >
            Create Booking
          </Button>
        </div>
      ),
    },
  ];

  return (
    <Box>
      <Box my={1} display='flex' justifyContent={'space-between'}>
        <Typography variant='h5'>Enquiries</Typography>
        <SmallSelect
          id='status-select'
          value={status}
          onChange={handleStatusChange}
          variant='outlined'
        >
          <MenuItem value={enquiryStatus.OPEN}>New</MenuItem>
          <MenuItem value={enquiryStatus.RESPONDED}>Responded</MenuItem>
          <MenuItem value={enquiryStatus.CONVERTED}>Converted</MenuItem>
          <MenuItem value={enquiryStatus.NONE}>All</MenuItem>
        </SmallSelect>
      </Box>
      {data && (
        <DataGrid
          rows={data.filter((row: Enquiry) => {
            return row.statusId === status || status === enquiryStatus.NONE;
          })}
          columns={columns}
          getRowId={(row) => row.enquiryId}
          pageSizeOptions={[25, 50, 100, 250]}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
        />
      )}
    </Box>
  );
};
export default Enquiries;
